import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { DrupalRESTService } from '../../../services/drupal-rest.service';
import { AuthService } from '../../../services/auth.service';
import { PermissionsService } from '../../../services/permissions.service';

interface Permission {
  key: string;
  label: string;
  description: string;
  category: string;
}

interface Role {
  id: string;
  label: string;
  description: string;
  weight: number;
  icon: string;
  memberCount: number;
  permissions: string[];
}

interface RoleAssignment {
  uid: number;
  roles: string[];
  name: string;
  mail: string;
}

@Component({
  selector: 'app-permissions',
  templateUrl: './permissions.component.html',
  styleUrls: ['./permissions.component.css']
})
export class PermissionsComponent implements OnInit, OnDestroy {
  private readonly hiddenRoleIds = new Set(['administrator']);

  selectedRole: Role | null = null;
  loading = true;
  saving = false;
  dirty = false;
  canEdit = false;

  permissionCategories: string[] = [];
  permissions: Permission[] = [];
  roles: Role[] = [];
  assignments: RoleAssignment[] = [];

  private originalPermissions: Record<string, string[]> = {};
  private studioSub: Subscription;

  constructor(
    private _drupalRESTService: DrupalRESTService,
    private _authService: AuthService,
    private _permissionsService: PermissionsService,
    private _snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    // BehaviorSubject fires the current studio immediately on subscribe,
    // which handles initial load and subsequent studio changes.
    this.studioSub = this._authService.selectedStudioChanged.subscribe(() => {
      this.selectedRole = null;
      this.loadConfig();
    });
  }

  ngOnDestroy(): void {
    if (this.studioSub) {
      this.studioSub.unsubscribe();
    }
  }

  private loadConfig(): void {
    this.loading = true;
    const studioId = this._authService.selectedStudio?.id;
    const url = studioId
      ? `/api/studio-permissions/config?studio_id=${studioId}`
      : '/api/studio-permissions/config';
    this._drupalRESTService.httpGET(url).subscribe({
      next: (res: any) => {
        if (res?.success && res.data) {
          this.buildFromConfig(res.data);
          this.loadAssignments();
        } else {
          this._snackBar.open('Failed to load permissions config.', 'Close', { duration: 5000 });
          this.loading = false;
        }
      },
      error: () => {
        this._snackBar.open('Error loading permissions config.', 'Close', { duration: 5000 });
        this.loading = false;
      },
    });
  }

  private buildFromConfig(data: any): void {
    // Build permissions list from config.
    const permDefs = data.permissions || {};
    this.permissions = Object.entries(permDefs).map(([key, def]: [string, any]) => ({
      key,
      label: def.label,
      description: def.description || '',
      category: def.category || 'Other',
    }));

    // Derive categories in order of appearance.
    const seen = new Set<string>();
    this.permissionCategories = [];
    for (const p of this.permissions) {
      if (!seen.has(p.category)) {
        seen.add(p.category);
        this.permissionCategories.push(p.category);
      }
    }

    // Build roles list from config.
    const roleDefs = data.roles || {};
    this.roles = Object.entries(roleDefs)
      .filter(([id]) => this.isVisibleRole(id))
      .map(([id, def]: [string, any]) => ({
        id,
        label: def.label,
        description: def.description || '',
        weight: def.weight ?? 0,
        icon: def.icon || 'group',
        memberCount: 0,
        permissions: [...(def.permissions || [])],
      }))
      .sort((a, b) => a.weight - b.weight);

    // Snapshot original permissions for dirty tracking.
    this.originalPermissions = {};
    for (const role of this.roles) {
      this.originalPermissions[role.id] = [...role.permissions];
    }
  }

  private loadAssignments(): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this.assignments = [];
      this.canEdit = false;
      this.updateMemberCounts();
      this.loading = false;
      return;
    }

    // Determine if current user can edit (admin or has staff_permissions).
    this.canEdit = this._permissionsService.has_access('view_admin_links')
      || this._permissionsService.hasStudioPermission('staff_permissions');

    this._drupalRESTService.httpGET(`/api/studio-permissions/studio/${studioId}/assignments`).subscribe({
      next: (res: any) => {
        this.assignments = res?.success ? (res.data || []) : [];
        this.updateMemberCounts();
        this.loading = false;
      },
      error: () => {
        // User may not have staff_permissions. That's OK, just show 0 counts.
        this.assignments = [];
        this.updateMemberCounts();
        this.loading = false;
      },
    });
  }

  private updateMemberCounts(): void {
    for (const role of this.roles) {
      role.memberCount = this.assignments.filter(a => a.roles.includes(role.id)).length;
    }
  }

  selectRole(role: Role): void {
    if (this.dirty) {
      if (!confirm('You have unsaved changes. Discard them?')) {
        return;
      }
      this.discardChanges();
    }
    this.selectedRole = this.selectedRole?.id === role.id ? null : role;
    this.dirty = false;
  }

  roleIcon(role: Role): string {
    return role.icon || 'group';
  }

  roleHasPermission(role: Role, permissionKey: string): boolean {
    return role.permissions.includes(permissionKey);
  }

  togglePermission(role: Role, permissionKey: string): void {
    const index = role.permissions.indexOf(permissionKey);
    if (index > -1) {
      role.permissions.splice(index, 1);
    } else {
      role.permissions.push(permissionKey);
    }
    this.dirty = this.hasChanges(role);
  }

  permissionsForCategory(category: string): Permission[] {
    return this.permissions.filter(p => p.category === category);
  }

  permissionCountLabel(role: Role): string {
    return `${role.permissions.length} of ${this.permissions.length}`;
  }

  savePermissions(): void {
    if (!this.selectedRole || this.saving) return;

    this.saving = true;
    const role = this.selectedRole;
    const studioId = this._authService.selectedStudio?.id;
    const url = studioId
      ? `/api/studio-permissions/roles/${role.id}/permissions?studio_id=${studioId}`
      : `/api/studio-permissions/roles/${role.id}/permissions`;

    this._drupalRESTService.httpPATCH(
      url,
      { permissions: role.permissions }
    ).subscribe({
      next: (res: any) => {
        this.saving = false;
        if (res?.success) {
          // Update snapshot so dirty tracking resets.
          this.originalPermissions[role.id] = [...role.permissions];
          this.dirty = false;
          this._snackBar.open('Permissions saved.', 'Close', { duration: 3000 });
          // Refresh the global cached permissions in case the current user's role was affected.
          this._permissionsService.loadStudioPermissions(true);
        } else {
          this._snackBar.open(res?.message || 'Failed to save permissions.', 'Close', { duration: 5000 });
        }
      },
      error: (err: any) => {
        this.saving = false;
        const msg = err?.error?.message || 'Error saving permissions.';
        this._snackBar.open(msg, 'Close', { duration: 5000 });
      },
    });
  }

  private hasChanges(role: Role): boolean {
    const original = this.originalPermissions[role.id] || [];
    if (role.permissions.length !== original.length) return true;
    const sorted = [...role.permissions].sort();
    const sortedOrig = [...original].sort();
    return sorted.some((v, i) => v !== sortedOrig[i]);
  }

  private discardChanges(): void {
    if (this.selectedRole) {
      const orig = this.originalPermissions[this.selectedRole.id];
      if (orig) {
        this.selectedRole.permissions = [...orig];
      }
    }
    this.dirty = false;
  }

  membersForRole(role: Role): RoleAssignment[] {
    return this.assignments.filter(a => a.roles.includes(role.id));
  }

  private isVisibleRole(roleId: string): boolean {
    return !this.hiddenRoleIds.has((roleId || '').toLowerCase());
  }
}
